(() => {
  if (customElements.get('mas-mobile-menu')) return;

  class MasMobileMenu extends HTMLElement {
    connectedCallback() {
      // The theme reparents drawers to body on open; retain listeners and state.
      if (this.initialized) return;
      this.rootPanel = this.querySelector('[data-mas-root]');
      if (!this.rootPanel) return;
      this.initialized = true;
      this.drawer = this.closest('hdt-drawer');
      this.panels = new Map([...this.querySelectorAll('[data-mas-panel]')].map(panel => [panel.id, panel]));
      this.activePanel = this.rootPanel;
      this.stack = [];
      this.classList.add('is-enhanced');

      this.querySelectorAll('[data-mas-open]').forEach(link => {
        link.setAttribute('role', 'button');
      });
      this.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const opener = event.target.closest('[data-mas-open]');
        if (opener && this.contains(opener)) {
          const panel = this.panels.get(opener.dataset.masOpen);
          if (!panel) return;
          event.preventDefault();
          this.stack.push({ panel: this.activePanel, opener, scroll: this.scroller?.scrollTop || 0 });
          opener.setAttribute('aria-expanded', 'true');
          this.showPanel(panel, 0, panel.querySelector('h2'));
          return;
        }
        if (event.target.closest('[data-mas-back]')) this.goBack();
      });
      this.addEventListener('keydown', event => {
        if (event.key === ' ' && event.target.matches('[data-mas-open]')) {
          event.preventDefault();
          event.target.click();
        }
      });
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-mas-filter]')) return;
        this.filterPanel(event.target.closest('[data-mas-panel]'), event.target.value);
      });
      this.drawer?.addEventListener('dialog:beforeOpen', () => {
        this.opener = document.activeElement?.closest('[aria-controls="menu_mobile"]');
        document.querySelectorAll('[aria-controls="menu_mobile"]').forEach(trigger => trigger.setAttribute('aria-expanded', 'true'));
        this.reset();
      });
      this.drawer?.addEventListener('dialog:afterClose', () => {
        if (!this.drawer.hasAttribute('open')) {
          this.reset();
          document.querySelectorAll('[aria-controls="menu_mobile"]').forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
          if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
        }
      });
      // The base drawer can open before this deferred component is upgraded.
      // Observe its actual state as well as events, so the first tap is never lost.
      if (this.drawer) {
        this.openObserver = new MutationObserver(() => {
          if (this.drawer.hasAttribute('open')) this.loadPanelMedia(this.activePanel);
        });
        this.openObserver.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
        if (this.drawer.hasAttribute('open')) this.loadPanelMedia(this.activePanel);
      }
    }

    get scroller() {
      return this.drawer?.shadowRoot?.querySelector('[part~="body"]');
    }

    showPanel(panel, scroll, focus) {
      this.activePanel.hidden = true;
      panel.hidden = false;
      this.loadPanelMedia(panel);
      this.activePanel = panel;
      if (this.scroller) this.scroller.scrollTop = scroll;
      focus?.focus({ preventScroll: true });
    }

    loadPanelMedia(panel) {
      // A closed drawer must not compete with the page's main image.
      panel.querySelectorAll('[data-mas-menu-src], [data-mas-menu-href]').forEach(media => {
        if (media.dataset.masMediaStarted) return;
        media.dataset.masMediaStarted = 'true';
        const attribute = media.hasAttribute('data-mas-menu-src') ? 'src' : 'href';
        const source = media.getAttribute('data-mas-menu-' + attribute);
        const art = media.closest('.mas-menu__art, .mas-menu__journal-thumb');
        const setState = state => { if (art) art.dataset.masMediaState = state; };
        setState('loading');
        media.addEventListener('load', () => setState('loaded'), { once: true });
        media.addEventListener('error', () => {
          setState('error');
          // Retry once; keep the icon and category link usable if the image fails.
          if (!media.dataset.masMediaRetried) {
            media.dataset.masMediaRetried = 'true';
            media.removeAttribute(attribute);
            media.setAttribute(attribute, source);
          }
        });
        if (media.localName === 'img') {
          media.loading = 'eager';
          media.decoding = 'async';
        }
        media.setAttribute(attribute, source);
        if (media.localName === 'img' && media.complete && media.naturalWidth > 0) setState('loaded');
      });
    }

    goBack() {
      const previous = this.stack.pop();
      if (!previous) return;
      previous.opener.setAttribute('aria-expanded', 'false');
      this.showPanel(previous.panel, previous.scroll, previous.opener);
    }

    filterPanel(panel, value) {
      const query = value.trim().toLocaleLowerCase();
      let visible = 0;
      panel.querySelectorAll('[data-mas-category]').forEach(row => {
        row.hidden = !row.textContent.toLocaleLowerCase().includes(query);
        if (!row.hidden) visible++;
      });
      panel.querySelector('[data-mas-empty]').hidden = visible !== 0;
    }

    reset() {
      this.stack = [];
      this.querySelectorAll('[data-mas-open]').forEach(link => link.setAttribute('aria-expanded', 'false'));
      this.querySelectorAll('[data-mas-filter]').forEach(input => {
        input.value = '';
        this.filterPanel(input.closest('[data-mas-panel]'), '');
      });
      this.showPanel(this.rootPanel, 0);
    }
  }

  customElements.define('mas-mobile-menu', MasMobileMenu);
})();
